import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { ChartData, ChartSeries } from '../../types';

type ChartRendererProps = {
  typeKey: string;
  data: ChartData;
};

const SERIES_PALETTE_KEYS = [
  'primary',
  'warning',
  'success',
  'error',
  'info',
] as const;

/** Convert series name to a stable object key (e.g. "Phq 9 Depression Test" → "phq_9_depression_test") */
const toDataKey = (name: string) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '_');

/**
 * Normalize an x-value to a day-level key.
 * ISO timestamps on the same calendar day collapse to one x-tick.
 * "previous" and non-date strings pass through as-is.
 */
const normalizeToDayKey = (x: string): string => {
  if (!x || x === 'previous') return 'previous';
  const d = new Date(x);
  if (isNaN(d.getTime())) return x;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** Sort key for ordering x-values chronologically. */
const sortKeyForX = (x: string): number => {
  if (x === 'previous') return 0;
  const d = new Date(x);
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

/** A marker dict as it arrives on the wire — e.g. { occurred_at, label, kind } from a semantic_interventions row. */
type ChartMarker = Record<string, unknown>;

/** Pull a marker's raw x-position (a timestamp) out of its loosely-typed payload. */
const markerRawX = (m: ChartMarker): string => {
  const raw = m.occurred_at ?? m.x ?? m.date;
  return typeof raw === 'string' || typeof raw === 'number' ? String(raw) : '';
};

/**
 * Merge multiple series into the flat array Recharts expects.
 * Rounds dates to the day so same-day assessments share one x-tick.
 * Each entry: { x: "Feb 9", series_a: 18, series_b: 12, ... }
 *
 * Marker positions are folded into the x-domain too: a categorical
 * <ReferenceLine x=…> only draws when its x matches a plotted tick, so a lever
 * pulled *between* trend points needs its day-key present as a category.
 */
const mergeSeriesData = (series: ChartSeries[], markers: ChartMarker[] = []) => {
  // Collect unique day-level keys with sort order
  const dayMap = new Map<string, number>(); // dayKey → sortKey
  for (const s of series) {
    for (const p of s.points ?? []) {
      const dayKey = normalizeToDayKey(p.x);
      if (!dayMap.has(dayKey)) {
        dayMap.set(dayKey, sortKeyForX(p.x));
      }
    }
  }

  // Anchor every marker to a category so its ReferenceLine always draws (see doc comment).
  for (const m of markers) {
    const raw = markerRawX(m);
    if (!raw) continue;
    const dayKey = normalizeToDayKey(raw);
    if (!dayMap.has(dayKey)) {
      dayMap.set(dayKey, sortKeyForX(raw));
    }
  }

  const sortedDays = [...dayMap.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => key);

  // Build a lookup: series dataKey → dayKey → y value.
  // If a series has multiple points on the same day, the last one wins.
  const seriesLookup = new Map<string, Map<string, number | undefined>>();
  for (const s of series) {
    const lookup = new Map<string, number | undefined>();
    for (const p of s.points ?? []) {
      // Treat null as missing — Recharts uses undefined for gaps, not zero
      lookup.set(normalizeToDayKey(p.x), p.y != null ? p.y : undefined);
    }
    seriesLookup.set(toDataKey(s.name), lookup);
  }

  return sortedDays.map(dayKey => {
    const label = dayKey === 'previous' ? 'Previous' : dayKey;
    const entry: Record<string, string | number | undefined> = { x: label };
    for (const [dataKey, lookup] of seriesLookup) {
      entry[dataKey] = lookup.get(dayKey);
    }
    return entry;
  });
};

export const ChartRenderer: React.FC<ChartRendererProps> = ({ data }) => {
  const theme = useTheme();
  const { series, goal_line, title, markers } = data;

  const hasData = series.some(s => (s.points?.length ?? 0) > 0);
  if (!hasData) return null;

  const markerList = (markers ?? []) as ChartMarker[];
  const merged = mergeSeriesData(series, markerList);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'grey.200',
        borderRadius: 2,
        p: '16px 20px',
      }}
    >
      {title && (
        <Typography variant="subtitle1" sx={{fontWeight: 700,  mb: 1 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={230}>
        <LineChart data={merged} margin={{ top: 20, right: 16, left: 0, bottom: 8 }}>
          <XAxis
            dataKey="x"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
            padding={{ left: 20, right: 20 }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            axisLine={false}
            tickLine={false}
            width={30}
          />

          <Legend
            verticalAlign="bottom"
            align="center"
            iconType="circle"
            iconSize={8}
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
          />

          {goal_line != null && (
            <ReferenceLine
              y={goal_line}
              stroke={theme.palette.text.disabled}
              strokeDasharray="6 4"
              label={{
                value: `Goal: ${goal_line}`,
                position: 'right',
                fontSize: 11,
                fill: theme.palette.text.disabled,
              }}
            />
          )}

          {/* Intervention markers — a vertical dashed line where a lever was
              pulled (the x-axis twin of the goal_line pattern above). */}
          {markerList.map((m, i) => {
            const raw = markerRawX(m);
            if (!raw) return null;
            const dayKey = normalizeToDayKey(raw);
            const x = dayKey === 'previous' ? 'Previous' : dayKey;
            const label = typeof m.label === 'string' ? m.label : '';
            return (
              <ReferenceLine
                key={`marker-${i}`}
                x={x}
                stroke={theme.palette.warning.dark}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                label={{
                  value: label,
                  position: 'top',
                  fontSize: 11,
                  fontWeight: 700,
                  fill: theme.palette.warning.dark,
                }}
              />
            );
          })}

          <Tooltip
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.shadows[2],
            }}
          />

          {series.map((s, i) => {
            const paletteKey = SERIES_PALETTE_KEYS[i % SERIES_PALETTE_KEYS.length];
            const color = theme.palette[paletteKey].dark;
            const dataKey = toDataKey(s.name);

            return (
              <Line
                key={dataKey}
                type="monotone"
                dataKey={dataKey}
                name={s.name}
                stroke={color}
                strokeWidth={2}
                dot={{ r: 5, fill: color, strokeWidth: 0 }}
                activeDot={{ r: 7 }}
                connectNulls
                isAnimationActive={false}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};
